import type { OfferingCategory } from "src/generated/hooks/form-service/requests";
import { z } from "zod";

export type OfferingOptionFormValues = {
  _reactId: string; // UI-only identity for React keys and drag-and-drop.
  id?: string;
  label: string;
  key: string;
};

export type OfferingCategoryFormValues = {
  id?: string;
  label: string;
  key: string;
  options: OfferingOptionFormValues[];
};

const requiredMessage = "This field is required";

const OfferingOptionFormSchema = z.object({
  label: z.string().trim().min(1, requiredMessage),
  key: z.string().trim().min(1, requiredMessage),
});

const OfferingCategoryFormSchema = z.object({
  label: z.string().trim().min(1, requiredMessage),
  key: z.string().trim().min(1, requiredMessage),
  options: z
    .array(OfferingOptionFormSchema)
    .min(1, "At least one option is required"),
});

export function createOfferingCategoryFormSchema(
  siblingCategoryKeys: readonly string[]
) {
  const takenKeys = new Set(
    siblingCategoryKeys.map((key) => key.trim()).filter((key) => key.length > 0)
  );

  return OfferingCategoryFormSchema.superRefine((values, ctx) => {
    // Check for duplicate category keys.
    if (values.key.trim() && takenKeys.has(values.key.trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This key is already in use",
        path: ["key"],
      });
    }

    // Check for duplicate option keys.
    const optionKeyCounts = new Map<string, number>();
    for (const option of values.options) {
      const key = option.key.trim();
      if (key) optionKeyCounts.set(key, (optionKeyCounts.get(key) ?? 0) + 1);
    }

    values.options.forEach((option, index) => {
      const key = option.key.trim();
      if (key && (optionKeyCounts.get(key) ?? 0) > 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "This key is already in use",
          path: ["options", index, "key"],
        });
      }
    });
  });
}

export function emptyOption(): OfferingOptionFormValues {
  return {
    _reactId: crypto.randomUUID(),
    label: "",
    key: "",
  };
}

export function emptyCategory(): OfferingCategoryFormValues {
  return {
    label: "",
    key: "",
    options: [emptyOption()],
  };
}

export function toFormValues(
  category: OfferingCategory
): OfferingCategoryFormValues {
  return {
    id: category.id,
    label: category.label,
    key: category.key,
    options: category.options.map((option) => ({
      _reactId: crypto.randomUUID(),
      id: option.id,
      label: option.label,
      key: option.key,
    })),
  };
}

export function toCreatePayload(values: OfferingCategoryFormValues) {
  return {
    label: values.label.trim(),
    key: values.key.trim(),
    options: values.options.map((option) => ({
      label: option.label.trim(),
      key: option.key.trim(),
    })),
  };
}

export function toUpdatePayload(values: OfferingCategoryFormValues) {
  return {
    label: values.label.trim(),
    key: values.key.trim(),
    options: values.options.map((option) => ({
      ...(option.id ? { id: option.id } : {}),
      label: option.label.trim(),
      key: option.key.trim(),
    })),
  };
}
